import React, { useState, useEffect } from 'react';
import { 
  Search, Globe, FileText, CheckCircle2, AlertCircle, Eye, Share2, 
  RefreshCw, Check, Copy, ExternalLink, Image as ImageIcon, 
  Smartphone, Monitor, Info, ShieldCheck, Tag, HelpCircle, Upload, Zap, Target, HardDrive,
  Sparkles, Link2
} from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';
import { PageSEO } from '../../types/siteContent';
import { STOCK_GALLERY_IMAGES } from '../../data/initialData';
import { AGENCY_CONFIG } from '../../config/agency';
import { MediaPickerModal } from './MediaPickerModal';

type PageKey = 'home' | 'sales' | 'rentals' | 'services' | 'team' | 'contacts';
type SubTab = 'editor' | 'preview' | 'audit' | 'global' | 'tracking';

const PAGES_INFO: Array<{ key: PageKey; name: string; path: string; icon: string }> = [
  { key: 'home', name: 'Начална страница', path: '/', icon: '🏠' },
  { key: 'sales', name: 'Имоти за продажба', path: '/prodazhbi', icon: '🏢' },
  { key: 'rentals', name: 'Имоти под наем', path: '/naemi', icon: '🔑' },
  { key: 'services', name: 'Брокерски услуги', path: '/uslugi', icon: '📑' },
  { key: 'team', name: 'Нашият екип', path: '/ekip', icon: '👥' },
  { key: 'contacts', name: 'Контакти & Офис', path: '/kontakti', icon: '📞' },
];

export const SEOManager: React.FC = () => {
  const { content, updateSection, saveContent, isSaving, lastSavedAt } = useSiteContent();
  const [selectedPage, setSelectedPage] = useState<PageKey>('home');
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('editor');
  const [serpViewMode, setSerpViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [saveSuccessMsg, setSaveSuccessMsg] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [pixelTestResult, setPixelTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Media Picker and Gallery for Favicon / Images
  const [mediaPicker, setMediaPicker] = useState<{
    isOpen: boolean;
    onSelect: (url: string) => void;
    title?: string;
    allowedType?: 'image' | 'video' | 'all';
  }>({
    isOpen: false,
    onSelect: () => {},
    title: 'Избор на Favicon от галерията',
    allowedType: 'image'
  });

  const [galleryImages, setGalleryImages] = useState<string[]>([
    '/imotencenterlogo-cropped.png',
    '/imotencenterlogo.png',
    ...STOCK_GALLERY_IMAGES
  ]);

  useEffect(() => {
    fetch('/media-manifest.json')
      .then(res => res.json())
      .then(files => {
        if (Array.isArray(files)) {
          const imgs = files
            .filter((f: any) => f.type === 'image' || ['png', 'jpg', 'jpeg', 'webp', 'svg', 'ico'].includes(f.extension))
            .map((f: any) => f.url);
          setGalleryImages(Array.from(new Set(['/imotencenterlogo-cropped.png', '/imotencenterlogo.png', ...imgs, ...STOCK_GALLERY_IMAGES])));
        }
      })
      .catch(() => {});
  }, []);

  const handleFaviconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          updateSection('branding', { faviconUrl: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Safely get current page SEO data with fallback
  const currentPageSEO: PageSEO = content.seo?.pages?.[selectedPage] || {
    id: selectedPage,
    pageName: PAGES_INFO.find(p => p.key === selectedPage)?.name || selectedPage,
    urlPath: PAGES_INFO.find(p => p.key === selectedPage)?.path || '/',
    metaTitle: '',
    metaDescription: '',
    focusKeyword: '',
    secondaryKeywords: '',
    h1: '',
    h2: '',
    pageDescription: '',
    ogImage: '',
    ogTitle: '',
    ogDescription: '',
    canonicalUrl: '',
    imageAltHero: '',
    imageAltSecondary: '',
    noIndex: false
  };

  const handleUpdateCurrentPage = (field: keyof PageSEO, value: any) => {
    const existingSeo = content.seo || {
      siteName: content.branding?.siteTitle || 'Варна Имотен Център',
      siteUrl: 'https://varna-imoti.bg',
      defaultOgImage: '',
      defaultFocusKeyword: 'имоти Варна',
      structuredDataEnabled: true,
      pages: {} as any
    };

    const updatedPages = {
      ...existingSeo.pages,
      [selectedPage]: {
        ...currentPageSEO,
        [field]: value
      }
    };

    updateSection('seo', {
      ...existingSeo,
      pages: updatedPages
    });
  };

  const handleUpdateGlobal = (field: string, value: any) => {
    const existingSeo = content.seo || {
      siteName: content.branding?.siteTitle || 'Варна Имотен Център',
      siteUrl: 'https://varna-imoti.bg',
      defaultOgImage: '',
      defaultFocusKeyword: 'имоти Варна',
      structuredDataEnabled: true,
      pages: {} as any
    };

    if (field === 'defaultOgImage') {
      const updatedPages = { ...(existingSeo.pages || {}) };
      // Also clear home page ogImage so it immediately inherits and uses defaultOgImage
      if (updatedPages.home) {
        updatedPages.home = {
          ...updatedPages.home,
          ogImage: ''
        };
      }
      updateSection('seo', {
        ...existingSeo,
        defaultOgImage: value,
        pages: updatedPages
      });
      return;
    }

    updateSection('seo', {
      ...existingSeo,
      [field]: value
    });
  };

  const handleApplyOgToAllPages = (imageUrl: string) => {
    const existingSeo = content.seo || {
      siteName: content.branding?.siteTitle || 'Варна Имотен Център',
      siteUrl: 'https://varna-imoti.bg',
      defaultOgImage: '',
      defaultFocusKeyword: 'имоти Варна',
      structuredDataEnabled: true,
      pages: {} as any
    };

    const updatedPages = { ...(existingSeo.pages || {}) };
    Object.keys(updatedPages).forEach((pageKey) => {
      if (updatedPages[pageKey]) {
        updatedPages[pageKey] = {
          ...updatedPages[pageKey],
          ogImage: ''
        };
      }
    });

    updateSection('seo', {
      ...existingSeo,
      defaultOgImage: imageUrl,
      pages: updatedPages
    });
  };

  const handleRegenerateAllCanonicals = () => {
    const existingSeo = content.seo || {
      siteName: content.branding?.siteTitle || 'Варна Имотен Център',
      siteUrl: 'https://varna-imoti.bg',
      defaultOgImage: '',
      defaultFocusKeyword: 'имоти Варна',
      structuredDataEnabled: true,
      pages: {} as any
    };

    const baseSiteUrl = (existingSeo.siteUrl || 'https://varna-imoti.bg').replace(/\/$/, '');
    const updatedPages = { ...(existingSeo.pages || {}) };

    PAGES_INFO.forEach(page => {
      const pageKey = page.key;
      const targetCanonical = `${baseSiteUrl}${page.path === '/' ? '/' : page.path}`;
      updatedPages[pageKey] = {
        ...(updatedPages[pageKey] || {}),
        canonicalUrl: targetCanonical
      } as any;
    });

    updateSection('seo', {
      ...existingSeo,
      pages: updatedPages
    });
  };

  const handleUpdateTracking = (field: string, value: string) => {
    const existingSeo: any = content.seo || {
      siteName: content.branding?.siteTitle || 'Варна Имотен Център',
      siteUrl: 'https://varna-imoti.bg',
      defaultOgImage: '',
      defaultFocusKeyword: 'имоти Варна',
      structuredDataEnabled: true,
      tracking: {},
      pages: {} as any
    };

    updateSection('seo', {
      ...existingSeo,
      tracking: {
        ...(existingSeo.tracking || {}),
        [field]: value
      }
    });
  };

  const handleSave = async () => {
    const success = await saveContent();
    if (success) {
      setSaveSuccessMsg(true);
      setTimeout(() => setSaveSuccessMsg(false), 3000);
    }
  };

  const handleTestPixel = () => {
    const pixelId = content.seo?.tracking?.metaPixelId?.trim();
    if (!pixelId) {
      setPixelTestResult({
        success: false,
        message: 'Моля първо въведете Вашия Meta Pixel ID в полето по-долу!'
      });
      return;
    }
    if (!/^\d{8,25}$/.test(pixelId)) {
      setPixelTestResult({
        success: false,
        message: `Внимание: Pixel ID "${pixelId}" обикновено съдържа само цифри (напр. 15-16 цифри). Проверете за букви или излишни знаци.`
      });
      return;
    }

    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      try {
        window.fbq('trackCustom', 'AdminPixelTest', {
          test_time: new Date().toISOString(),
          pixel_id: pixelId
        });
        setPixelTestResult({
          success: true,
          message: `Тестово събитие "AdminPixelTest" беше успешно изпратено към Meta Pixel (ID: ${pixelId})! Отворете Meta Events Manager -> Test Events, за да видите активния трафик в реално време.`
        });
      } catch (err: any) {
        setPixelTestResult({
          success: true,
          message: `Meta Pixel ID ${pixelId} е зареден и активен в браузъра.`
        });
      }
    } else {
      setPixelTestResult({
        success: true,
        message: `Meta Pixel ID ${pixelId} е записан успешно. Съгласно европейския регламент (GDPR), Facebook пикселът се активира веднага при посещение от клиент с маркетингово съгласие.`
      });
    }
  };

  // --- Real-Time On-Page SEO Checklist & Scoring Engine ---
  const audit = React.useMemo(() => {
    const kw = (currentPageSEO.focusKeyword || '').trim().toLowerCase();
    const title = (currentPageSEO.metaTitle || '').toLowerCase();
    const desc = (currentPageSEO.metaDescription || '').toLowerCase();
    const h1 = (currentPageSEO.h1 || '').toLowerCase();
    const h2 = (currentPageSEO.h2 || '').toLowerCase();
    const pageDesc = (currentPageSEO.pageDescription || '').toLowerCase();
    const alt = (currentPageSEO.imageAltHero || '').toLowerCase();
    const ogImg = currentPageSEO.ogImage;

    const checks = [
      {
        id: 'kw-set',
        label: 'Зададена фокусна ключова дума',
        passed: kw.length > 2,
        weight: 10,
        tip: 'Въведете основна фраза, за която искате страницата да се класира (напр. "имоти под наем Варна").'
      },
      {
        id: 'kw-in-title',
        label: 'Фокусната дума присъства в Meta Title',
        passed: kw.length > 2 && title.includes(kw),
        weight: 15,
        tip: 'Meta заглавието трябва задължително да включва фокусната ключова дума.'
      },
      {
        id: 'title-length',
        label: 'Оптимална дължина на Meta Title (40-65 символа)',
        passed: currentPageSEO.metaTitle.length >= 40 && currentPageSEO.metaTitle.length <= 65,
        weight: 10,
        tip: `Текущо: ${currentPageSEO.metaTitle.length} символа. Препоръчително е между 40 и 65 символа за пълно показване в Google.`
      },
      {
        id: 'kw-in-desc',
        label: 'Фокусната дума присъства в Meta Description',
        passed: kw.length > 2 && desc.includes(kw),
        weight: 15,
        tip: 'Google удебелява ключовите думи в мета описанието, когато съвпадат с търсенето на клиента.'
      },
      {
        id: 'desc-length',
        label: 'Оптимална дължина на Meta Description (120-165 символа)',
        passed: currentPageSEO.metaDescription.length >= 120 && currentPageSEO.metaDescription.length <= 165,
        weight: 10,
        tip: `Текущо: ${currentPageSEO.metaDescription.length} символа. Препоръчително е между 120 и 165 символа.`
      },
      {
        id: 'kw-in-h1',
        label: 'Фокусната дума присъства в H1 заглавието',
        passed: kw.length > 2 && h1.includes(kw),
        weight: 15,
        tip: 'Основният H1 таг на страницата е най-силният вътрешен он-пейдж фактор за класиране.'
      },
      {
        id: 'kw-in-h2-or-content',
        label: 'Фокусната дума присъства в H2 или съдържанието',
        passed: kw.length > 2 && (h2.includes(kw) || pageDesc.includes(kw)),
        weight: 10,
        tip: 'Ключовата дума трябва да се среща естествено и в подзаглавията (H2) или описанието на страницата.'
      },
      {
        id: 'kw-in-alt',
        label: 'Фокусната дума присъства в Alt текста на изображенията',
        passed: kw.length > 2 && alt.includes(kw),
        weight: 10,
        tip: 'Помага за отлично позициониране и в Google Images (Търсене по изображения).'
      },
      {
        id: 'has-og-image',
        label: 'Зададено Open Graph (OG) изображение за споделяне',
        passed: !!ogImg && ogImg.startsWith('http'),
        weight: 5,
        tip: 'Осигурява красива карта с голямо изображение при споделяне във Facebook, LinkedIn и Viber.'
      }
    ];

    const totalScore = checks.reduce((sum, c) => sum + (c.passed ? c.weight : 0), 0);
    return {
      score: totalScore,
      checks
    };
  }, [currentPageSEO]);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(id);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden text-slate-800">
      {/* Top Header Bar */}
      <div className="bg-[#0F1E32] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="p-2 bg-[#C5A46E]/20 text-[#C5A46E] rounded-lg">
              <Search className="w-5 h-5" />
            </span>
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tight text-white">
              SEO Мениджър на Сайта
            </h2>
          </div>
          <p className="text-xs text-slate-300 mt-1.5 max-w-2xl font-normal">
            Пълно on-page и техническо SEO управление за всяка страница: Meta заглавия, описания, фокусни ключови думи, H1/H2 тагове, Alt текстове и Open Graph изображения.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {saveSuccessMsg && (
            <span className="text-xs font-bold text-emerald-400 bg-emerald-950/80 px-3 py-2 rounded-lg border border-emerald-500/30 flex items-center gap-1.5 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4" /> Запазено успешно!
            </span>
          )}
          <button
            id="seo-save-changes-btn"
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2.5 bg-[#C5A46E] hover:bg-[#B1905E] text-white font-bold rounded-lg text-xs tracking-wider uppercase shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Запази SEO Настройки
          </button>
        </div>
      </div>

      {/* Navigation Subtabs: Editor | SERP & Social Preview | SEO Audit | Global Settings */}
      <div className="flex border-b border-slate-200 bg-slate-50 px-6 overflow-x-auto gap-2">
        <button
          onClick={() => setActiveSubTab('editor')}
          className={`py-3.5 px-4 text-xs font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 shrink-0 ${
            activeSubTab === 'editor'
              ? 'border-[#C5A46E] text-[#0F1E32] bg-white'
              : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
          }`}
        >
          <FileText className="w-4 h-4 text-[#C5A46E]" />
          Редактор по страници
        </button>

        <button
          onClick={() => setActiveSubTab('preview')}
          className={`py-3.5 px-4 text-xs font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 shrink-0 ${
            activeSubTab === 'preview'
              ? 'border-[#C5A46E] text-[#0F1E32] bg-white'
              : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
          }`}
        >
          <Eye className="w-4 h-4 text-[#C5A46E]" />
          Google SERP & Социални Карти
        </button>

        <button
          onClick={() => setActiveSubTab('audit')}
          className={`py-3.5 px-4 text-xs font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 shrink-0 ${
            activeSubTab === 'audit'
              ? 'border-[#C5A46E] text-[#0F1E32] bg-white'
              : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
          }`}
        >
          <ShieldCheck className="w-4 h-4 text-[#C5A46E]" />
          SEO Одит & Чеклист ({audit.score}/100)
        </button>

        <button
          onClick={() => setActiveSubTab('global')}
          className={`py-3.5 px-4 text-xs font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 shrink-0 ${
            activeSubTab === 'global'
              ? 'border-[#C5A46E] text-[#0F1E32] bg-white'
              : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
          }`}
        >
          <Globe className="w-4 h-4 text-[#C5A46E]" />
          Глобални SEO & Sitemap
        </button>

        <button
          onClick={() => setActiveSubTab('tracking')}
          className={`py-3.5 px-4 text-xs font-bold uppercase tracking-wider border-b-2 transition flex items-center gap-2 shrink-0 ${
            activeSubTab === 'tracking'
              ? 'border-[#1877F2] text-[#0F1E32] bg-white shadow-sm'
              : 'border-transparent text-slate-500 hover:text-[#0F1E32]'
          }`}
        >
          <Target className="w-4 h-4 text-[#1877F2]" />
          <span>Маркетинг & Meta Pixel</span>
          {content.seo?.tracking?.metaPixelId ? (
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Активен пиксел"></span>
          ) : (
            <span className="text-[9px] bg-slate-200 text-slate-600 px-1 py-0.5 rounded font-mono font-bold">Pixel</span>
          )}
        </button>
      </div>

      {/* Main Workspace */}
      <div className="p-6 md:p-8">
        {/* Page Selector Pills (Active for Editor, Preview, and Audit) */}
        {activeSubTab !== 'global' && activeSubTab !== 'tracking' && (
          <div className="mb-8">
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">
              ИЗБЕРЕТЕ СТРАНИЦА ЗА ОПТИМИЗАЦИЯ:
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
              {PAGES_INFO.map(page => {
                const isSelected = selectedPage === page.key;
                return (
                  <button
                    key={page.key}
                    onClick={() => setSelectedPage(page.key)}
                    className={`py-3 px-3.5 rounded-xl border text-left transition flex flex-col gap-1 cursor-pointer ${
                      isSelected
                        ? 'bg-[#0F1E32] text-white border-[#0F1E32] shadow-md ring-2 ring-[#C5A46E]'
                        : 'bg-slate-50 hover:bg-slate-150 text-slate-700 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base">{page.icon}</span>
                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${
                        isSelected ? 'bg-amber-500/30 text-amber-300' : 'bg-slate-200 text-slate-600'
                      }`}>
                        {page.key}
                      </span>
                    </div>
                    <span className="text-xs font-extrabold truncate mt-1">{page.name}</span>
                    <span className={`text-[10px] font-mono truncate ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                      {page.path}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* 1. EDITOR TAB */}
        {activeSubTab === 'editor' && (
          <div className="space-y-8">
            {/* Action header */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 md:p-5">
              <span className="text-[10px] font-black uppercase tracking-widest text-[#C5A46E]">
                Оптимизация на: {PAGES_INFO.find(p => p.key === selectedPage)?.name} ({currentPageSEO.urlPath})
              </span>
              <h4 className="text-sm font-bold text-[#0F1E32] mt-0.5">
                Попълнете Meta данните и целевите ключови думи за Google
              </h4>
            </div>

            {/* Form Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column: On-page Keywords & Head Tags */}
              <div className="space-y-6">
                <div className="border-b border-slate-100 pb-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#0F1E32] flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#C5A46E]" />
                    Ключови думи & Търсачки (On-Page SEO)
                  </h3>
                </div>

                {/* Focus Keyword */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Фокусна ключова дума (Focus Keyword) *
                    </label>
                    <span className="text-[11px] text-slate-400 font-mono">
                      напр. имоти под наем Варна
                    </span>
                  </div>
                  <input
                    type="text"
                    value={currentPageSEO.focusKeyword}
                    onChange={(e) => handleUpdateCurrentPage('focusKeyword', e.target.value)}
                    placeholder="напр. имоти за продажба Варна"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-semibold text-slate-900"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Главната фраза, по която искате потребителите да намират тази страница в Google.
                  </p>
                </div>

                {/* Secondary Keywords */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                    Второстепенни ключови думи (запетая разделени)
                  </label>
                  <input
                    type="text"
                    value={currentPageSEO.secondaryKeywords}
                    onChange={(e) => handleUpdateCurrentPage('secondaryKeywords', e.target.value)}
                    placeholder="апартаменти Варна, купува жилище Варна, агенция имоти Варна"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                </div>

                {/* Meta Title */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Meta Title (Заглавие за Google) *
                    </label>
                    <span className={`text-[11px] font-mono font-bold ${
                      currentPageSEO.metaTitle.length >= 40 && currentPageSEO.metaTitle.length <= 65
                        ? 'text-emerald-600'
                        : currentPageSEO.metaTitle.length > 65
                        ? 'text-red-500'
                        : 'text-amber-600'
                    }`}>
                      {currentPageSEO.metaTitle.length} / 60 символа
                    </span>
                  </div>
                  <input
                    type="text"
                    value={currentPageSEO.metaTitle}
                    onChange={(e) => handleUpdateCurrentPage('metaTitle', e.target.value)}
                    placeholder="Варна Имотен Център - Продажби и Наеми на Имоти във Варна"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  {/* Progress bar */}
                  <div className="w-full bg-slate-200 h-1.5 rounded-full mt-1.5 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        currentPageSEO.metaTitle.length > 65
                          ? 'bg-red-500'
                          : currentPageSEO.metaTitle.length >= 40
                          ? 'bg-emerald-500'
                          : 'bg-amber-400'
                      }`}
                      style={{ width: `${Math.min(100, (currentPageSEO.metaTitle.length / 60) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Meta Description */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                      Meta Description (Снипет текст за търсачки) *
                    </label>
                    <span className={`text-[11px] font-mono font-bold ${
                      currentPageSEO.metaDescription.length >= 120 && currentPageSEO.metaDescription.length <= 165
                        ? 'text-emerald-600'
                        : currentPageSEO.metaDescription.length > 165
                        ? 'text-red-500'
                        : 'text-amber-600'
                    }`}>
                      {currentPageSEO.metaDescription.length} / 160 символа
                    </span>
                  </div>
                  <textarea
                    rows={3}
                    value={currentPageSEO.metaDescription}
                    onChange={(e) => handleUpdateCurrentPage('metaDescription', e.target.value)}
                    placeholder="Търсите имот във Варна? Ние предлагаме 100% проверени оферти..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none leading-relaxed"
                  />
                  {/* Progress bar */}
                  <div className="w-full bg-slate-200 h-1.5 rounded-full mt-1 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        currentPageSEO.metaDescription.length > 165
                          ? 'bg-red-500'
                          : currentPageSEO.metaDescription.length >= 120
                          ? 'bg-emerald-500'
                          : 'bg-amber-400'
                      }`}
                      style={{ width: `${Math.min(100, (currentPageSEO.metaDescription.length / 160) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Canonical URL & Indexing */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                        Каноничен URL (Canonical)
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          const baseSiteUrl = (content.seo?.siteUrl || 'https://varna-imoti.bg').replace(/\/$/, '');
                          const pagePath = PAGES_INFO.find(p => p.key === selectedPage)?.path || '/';
                          const defaultCanonical = `${baseSiteUrl}${pagePath === '/' ? '/' : pagePath}`;
                          handleUpdateCurrentPage('canonicalUrl', defaultCanonical);
                        }}
                        className="text-[11px] font-bold text-[#C5A46E] hover:text-[#0F1E32] transition flex items-center gap-1 cursor-pointer"
                        title="Попълни автоматично според избраната страница"
                      >
                        <Sparkles className="w-3 h-3" />
                        <span>Авто-попълване</span>
                      </button>
                    </div>
                    <input
                      type="text"
                      value={currentPageSEO.canonicalUrl || ''}
                      onChange={(e) => handleUpdateCurrentPage('canonicalUrl', e.target.value)}
                      placeholder={`${(content.seo?.siteUrl || 'https://varna-imoti.bg').replace(/\/$/, '')}${PAGES_INFO.find(p => p.key === selectedPage)?.path || '/'}`}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-mono"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      Ако оставите празно, сайтът автоматично използва адреса на тази страница ({PAGES_INFO.find(p => p.key === selectedPage)?.path}).
                    </p>
                  </div>
                  <div className="flex items-center pt-5">
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700">
                      <input
                        type="checkbox"
                        checked={!!currentPageSEO.noIndex}
                        onChange={(e) => handleUpdateCurrentPage('noIndex', e.target.checked)}
                        className="w-4 h-4 rounded text-red-600 focus:ring-red-500"
                      />
                      <span>Забрани индексиране (noindex, nofollow)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column: H1, H2, Page Description & Alt Texts & OG Image */}
              <div className="space-y-6">
                <div className="border-b border-slate-100 pb-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#0F1E32] flex items-center gap-2">
                    <Globe className="w-4 h-4 text-[#C5A46E]" />
                    H1, H2, Описание & Alt Текстове на изображения
                  </h3>
                </div>

                {/* H1 Primary Header */}
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-bold text-slate-700 uppercase tracking-wide">
                      H1 Основно Заглавие на страницата *
                    </label>
                    <span className="text-[10px] text-amber-600 font-bold uppercase tracking-widest bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                      Главен H1 таг
                    </span>
                  </div>
                  <input
                    type="text"
                    value={currentPageSEO.h1}
                    onChange={(e) => handleUpdateCurrentPage('h1', e.target.value)}
                    placeholder="напр. Имоти за продажба във Варна"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-bold text-slate-900"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Показва се като основно голямо заглавие на съответната страница.
                  </p>
                </div>

                {/* H2 Secondary Header */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                    H2 Второстепенно Заглавие (Секционно) *
                  </label>
                  <input
                    type="text"
                    value={currentPageSEO.h2}
                    onChange={(e) => handleUpdateCurrentPage('h2', e.target.value)}
                    placeholder="Купете сигурно своето ново жилище или инвестиционен имот"
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                </div>

                {/* Page Description */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                    Описание на страницата (Текстово въведение)
                  </label>
                  <textarea
                    rows={2}
                    value={currentPageSEO.pageDescription}
                    onChange={(e) => handleUpdateCurrentPage('pageDescription', e.target.value)}
                    placeholder="Подробно описание, което се показва под заглавието на страницата..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none leading-relaxed"
                  />
                </div>

                {/* Alt Texts for Images */}
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800 uppercase tracking-wider">
                    <ImageIcon className="w-4 h-4 text-[#C5A46E]" />
                    Alt текстове на изображенията (Image Alt Attributes)
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
                      Alt текст на главното изображение (Hero Banner Image)
                    </label>
                    <input
                      type="text"
                      value={currentPageSEO.imageAltHero}
                      onChange={(e) => handleUpdateCurrentPage('imageAltHero', e.target.value)}
                      placeholder="Модерна жилищна сграда и имоти във Варна - Варна Имотен Център"
                      className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      Включете фокусната дума "{currentPageSEO.focusKeyword || 'имоти'}" в alt текста за по-добро класиране в Google Images.
                    </span>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wide mb-1">
                      Alt текст на второстепенни изображения / Карта
                    </label>
                    <input
                      type="text"
                      value={currentPageSEO.imageAltSecondary}
                      onChange={(e) => handleUpdateCurrentPage('imageAltSecondary', e.target.value)}
                      placeholder="Интериор на апартамент във Варна"
                      className="w-full bg-white border border-slate-200 rounded-lg py-2 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Open Graph Image */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                    Open Graph (OG) Изображение за тази страница (1200x630px)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={currentPageSEO.ogImage}
                      onChange={(e) => handleUpdateCurrentPage('ogImage', e.target.value)}
                      placeholder="https://... или /hero.webp (1200x630)"
                      className="flex-grow bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setMediaPicker({
                        isOpen: true,
                        title: `Избор на OG изображение за страница "${currentPageSEO.pageName}"`,
                        allowedType: 'image',
                        onSelect: (url) => handleUpdateCurrentPage('ogImage', url)
                      })}
                      className="px-3 py-2 bg-[#0F1E32] hover:bg-[#1a3354] text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 shrink-0"
                    >
                      <ImageIcon className="w-3.5 h-3.5 text-[#C5A46E]" />
                      <span>Галерия</span>
                    </button>
                    {currentPageSEO.ogImage && (
                      <button
                        type="button"
                        onClick={() => handleUpdateCurrentPage('ogImage', '')}
                        className="px-2.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs rounded-lg transition-colors"
                        title="Нулирай (ще ползва общото)"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* OG Image quick preview & stock picker */}
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <div className="w-28 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0 relative shadow-sm">
                      <img
                        src={currentPageSEO.ogImage || content.seo?.defaultOgImage || '/hero.webp'}
                        alt="OG Preview"
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/hero.webp';
                        }}
                      />
                      <div className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[8px] px-1 rounded font-mono">
                        {currentPageSEO.ogImage ? 'Персонална' : 'По подразбиране'}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] text-slate-400 font-bold uppercase mr-1">Бърз избор:</span>
                      <button
                        type="button"
                        onClick={() => handleUpdateCurrentPage('ogImage', '/hero.webp')}
                        className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-[#C5A46E]/20 hover:text-[#0F1E32] border border-slate-200 rounded font-medium transition"
                      >
                        🏢 Херо
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateCurrentPage('ogImage', '/imotencenterlogo.png')}
                        className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-[#C5A46E]/20 hover:text-[#0F1E32] border border-slate-200 rounded font-medium transition"
                      >
                        👑 Лого
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateCurrentPage('ogImage', '/imotencenterlogo-cropped.png')}
                        className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-[#C5A46E]/20 hover:text-[#0F1E32] border border-slate-200 rounded font-medium transition"
                      >
                        🌟 Златен знак
                      </button>
                      {STOCK_GALLERY_IMAGES.slice(0, 3).map((img, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => handleUpdateCurrentPage('ogImage', img)}
                          className="w-7 h-7 rounded overflow-hidden border border-slate-200 hover:scale-105 transition shrink-0"
                          title="Избери тази снимка"
                        >
                          <img src={img} alt="Thumb" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 2. PREVIEW TAB (Google SERP Snippet & Social Share Card) */}
        {activeSubTab === 'preview' && (
          <div className="space-y-10">
            {/* Google SERP Snippet Preview */}
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-slate-200 pb-4">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#C5A46E]">
                    ИНТЕРАКТИВЕН ИЗГЛЕД В GOOGLE ТЪРСЕНЕТО
                  </span>
                  <h3 className="text-base font-extrabold text-[#0F1E32]">
                    Как потребителите ще виждат {PAGES_INFO.find(p => p.key === selectedPage)?.name} в Google:
                  </h3>
                </div>

                <div className="flex items-center bg-white p-1 rounded-xl border border-slate-200 shadow-sm">
                  <button
                    onClick={() => setSerpViewMode('desktop')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      serpViewMode === 'desktop' ? 'bg-[#0F1E32] text-white shadow' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Monitor className="w-3.5 h-3.5" /> Десктоп
                  </button>
                  <button
                    onClick={() => setSerpViewMode('mobile')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${
                      serpViewMode === 'mobile' ? 'bg-[#0F1E32] text-white shadow' : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Smartphone className="w-3.5 h-3.5" /> Мобилен
                  </button>
                </div>
              </div>

              {/* SERP Box */}
              <div className="max-w-2xl bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-200 font-sans">
                {serpViewMode === 'desktop' ? (
                  <div className="space-y-1 text-left">
                    {/* Breadcrumbs & URL */}
                    <div className="flex items-center gap-2 text-xs text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs text-amber-700 font-bold border border-slate-200">
                        VI
                      </div>
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-semibold text-slate-900">
                          {content.seo?.siteName || 'Варна Имотен Център'}
                        </span>
                        <span className="text-[11px] text-slate-500 font-mono">
                          {content.seo?.siteUrl || 'https://varna-imoti.bg'}{currentPageSEO.urlPath}
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <h4 className="text-lg md:text-xl text-[#1a0dab] hover:underline cursor-pointer font-medium pt-1 leading-snug">
                      {currentPageSEO.metaTitle || 'Варна Имотен Център - Продажби и Наеми на Имоти във Варна'}
                    </h4>

                    {/* Snippet Description */}
                    <p className="text-xs md:text-sm text-[#4d5156] leading-relaxed pt-1 font-normal">
                      <span className="text-slate-400 font-medium">
                        {new Date().toLocaleDateString('bg-BG', { day: 'numeric', month: 'short', year: 'numeric' })} —{' '}
                      </span>
                      {currentPageSEO.metaDescription ||
                        'Търсите имот във Варна? Ние предлагаме 100% проверени оферти за покупка и наем на апартаменти и къщи...'}
                    </p>
                  </div>
                ) : (
                  /* Mobile SERP */
                  <div className="space-y-1.5 text-left border-l-2 border-amber-400 pl-3">
                    <div className="flex items-center gap-2 text-xs">
                      <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center text-[10px] text-amber-700 font-bold">
                        VI
                      </div>
                      <span className="text-xs text-slate-900 font-semibold">{content.seo?.siteName || 'Варна Имотен Център'}</span>
                      <span className="text-[10px] text-slate-400">› {selectedPage}</span>
                    </div>

                    <h4 className="text-base text-[#1a0dab] font-medium leading-tight">
                      {currentPageSEO.metaTitle || 'Варна Имотен Център - Продажби и Наеми'}
                    </h4>

                    <p className="text-xs text-[#4d5156] leading-relaxed">
                      {currentPageSEO.metaDescription || 'Търсите имот във Варна? Ние предлагаме 100% проверени оферти...'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Share Card Preview (Facebook, LinkedIn, Viber) */}
            <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-200">
              <div className="mb-6 border-b border-slate-200 pb-4">
                <span className="text-[10px] font-black uppercase tracking-widest text-[#C5A46E] flex items-center gap-1.5">
                  <Share2 className="w-4 h-4" /> СОЦИАЛНИ МРЕЖИ (OPEN GRAPH PREVIEW)
                </span>
                <h3 className="text-base font-extrabold text-[#0F1E32] mt-0.5">
                  Преглед при споделяне във Facebook, LinkedIn, Viber и WhatsApp
                </h3>
              </div>

              <div className="max-w-xl bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-md">
                {/* 1200x630 aspect ratio preview */}
                <div className="w-full aspect-[1.91/1] bg-slate-150 relative overflow-hidden">
                  <img
                    src={currentPageSEO.ogImage || content.seo?.defaultOgImage || STOCK_GALLERY_IMAGES[0]}
                    alt="Social Card Preview"
                    referrerPolicy="no-referrer"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded font-mono font-bold">
                    1200 × 630 OG Image
                  </div>
                </div>

                <div className="p-4 bg-slate-50 border-t border-slate-150 text-left">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest font-mono">
                    {content.seo?.siteUrl ? content.seo.siteUrl.replace(/^https?:\/\//, '') : 'varna-imoti.bg'}
                  </span>
                  <h4 className="font-bold text-sm text-[#0F1E32] mt-1 line-clamp-1">
                    {currentPageSEO.ogTitle || currentPageSEO.metaTitle || 'Варна Имотен Център'}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed font-normal">
                    {currentPageSEO.ogDescription || currentPageSEO.metaDescription || '100% проверени оферти за имоти във Варна.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 3. SEO AUDIT & CHECKLIST TAB */}
        {activeSubTab === 'audit' && (
          <div className="space-y-8">
            {/* Audit Score Hero Card */}
            <div className="bg-[#0F1E32] text-white rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
              <div>
                <span className="text-xs font-bold text-[#C5A46E] uppercase tracking-widest">
                  ON-PAGE SEO ОЦЕНКА ЗА: {PAGES_INFO.find(p => p.key === selectedPage)?.name}
                </span>
                <h3 className="text-2xl font-black mt-1 uppercase tracking-tight">
                  {audit.score >= 85 ? 'Отлично SEO Оптимизирана! 🚀' : audit.score >= 60 ? 'Добро ниво, но има препоръки ⚠️' : 'Нуждае се от SEO подобрения 🛠️'}
                </h3>
                <p className="text-xs text-slate-300 mt-1 max-w-xl font-normal">
                  Фокусната дума <span className="text-amber-400 font-bold font-mono">"{currentPageSEO.focusKeyword || 'не е зададена'}"</span> се оценява по 9 ключови алгоритмични фактора на Google за най-високо позициониране.
                </p>
              </div>

              {/* Circular or pill score gauge */}
              <div className="flex items-center gap-4 shrink-0 bg-[#15253A] px-6 py-4 rounded-2xl border border-slate-700">
                <div className="text-center">
                  <span className={`text-4xl md:text-5xl font-black font-sans ${
                    audit.score >= 85 ? 'text-emerald-400' : audit.score >= 60 ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {audit.score}
                  </span>
                  <span className="text-sm font-bold text-slate-400">/100</span>
                  <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 mt-0.5">
                    SEO Score
                  </div>
                </div>
              </div>
            </div>

            {/* Checklist Items */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                Детайлен анализ на критериите:
              </h4>

              {audit.checks.map(check => (
                <div
                  key={check.id}
                  className={`p-4 rounded-xl border transition flex items-start justify-between gap-4 ${
                    check.passed
                      ? 'bg-emerald-50/60 border-emerald-200 text-slate-800'
                      : 'bg-slate-50 border-slate-200 text-slate-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0">
                      {check.passed ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-500" />
                      )}
                    </span>
                    <div>
                      <h5 className={`text-xs font-bold ${check.passed ? 'text-emerald-900' : 'text-slate-800'}`}>
                        {check.label}
                      </h5>
                      <p className="text-[11px] text-slate-500 mt-0.5 font-normal leading-relaxed">
                        {check.tip}
                      </p>
                    </div>
                  </div>

                  <span className={`text-[10px] font-bold px-2 py-1 rounded font-mono uppercase shrink-0 ${
                    check.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {check.passed ? `+${check.weight} т. (Покрито)` : '0 т.'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. GLOBAL SEO SETTINGS TAB */}
        {activeSubTab === 'global' && (
          <div className="space-y-8">
            <div className="border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#0F1E32] flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#C5A46E]" />
                Общи технически SEO настройки за целия сайт
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Име на сайта за търсачки (Site Name)
                </label>
                <input
                  type="text"
                  value={content.seo?.siteName || ''}
                  onChange={(e) => handleUpdateGlobal('siteName', e.target.value)}
                  placeholder="Варна Имотен Център"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-bold"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">
                    Каноничен базов URL адрес (Site Base URL)
                  </label>
                  <button
                    type="button"
                    onClick={handleRegenerateAllCanonicals}
                    className="text-[11px] font-bold text-[#C5A46E] hover:text-[#0F1E32] transition flex items-center gap-1 cursor-pointer"
                    title="Приложи този базов домейн към каноничния URL на всички страници"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Синхронизирай всички страници</span>
                  </button>
                </div>
                <input
                  type="text"
                  value={content.seo?.siteUrl || ''}
                  onChange={(e) => handleUpdateGlobal('siteUrl', e.target.value)}
                  placeholder="https://varna-imoti.bg"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-mono"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  Сайтът автоматично добавя този домейн пред каноничния URL на всяка страница (напр. /prodazhbi, /naemi, /imoti/...).
                </p>
              </div>

              <div className="bg-white p-4 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
                    Основно Open Graph изображение по подразбиране (Facebook, Viber, WhatsApp)
                  </label>
                  <span className="text-[10px] text-slate-500 font-medium">1200 × 630 px</span>
                </div>
                
                {/* Visual Preview & Selector */}
                <div className="flex flex-col sm:flex-row gap-4 items-start mb-2">
                  <div className="w-full sm:w-48 aspect-[1.91/1] rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative shrink-0 shadow-sm">
                    <img
                      src={content.seo?.defaultOgImage || '/hero.webp'}
                      alt="OG Default Preview"
                      referrerPolicy="no-referrer"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/hero.webp';
                      }}
                    />
                    <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-mono font-bold">
                      1200×630
                    </div>
                  </div>

                  <div className="flex-grow space-y-2.5 w-full">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={content.seo?.defaultOgImage || ''}
                        onChange={(e) => handleUpdateGlobal('defaultOgImage', e.target.value)}
                        placeholder="https://... или /hero.webp"
                        className="flex-grow bg-slate-50 border border-slate-200 rounded-lg py-2 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none font-mono"
                      />
                      <button
                        type="button"
                        onClick={() => setMediaPicker({
                          isOpen: true,
                          title: 'Избор на Open Graph изображение по подразбиране',
                          allowedType: 'image',
                          onSelect: (url) => handleUpdateGlobal('defaultOgImage', url)
                        })}
                        className="px-3 py-2 bg-[#0F1E32] hover:bg-[#1a3354] text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 shrink-0"
                      >
                        <ImageIcon className="w-3.5 h-3.5 text-[#C5A46E]" />
                        <span>Галерия</span>
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center pt-0.5">
                      <span className="text-[10px] text-slate-400 font-bold uppercase">Бърз избор:</span>
                      <button
                        type="button"
                        onClick={() => handleUpdateGlobal('defaultOgImage', '/hero.webp')}
                        className={`text-[11px] px-2.5 py-1 rounded-md border transition-all flex items-center gap-1 font-medium ${
                          (content.seo?.defaultOgImage === '/hero.webp' || !content.seo?.defaultOgImage)
                            ? 'bg-[#C5A46E]/20 border-[#C5A46E] text-[#0F1E32] font-bold'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span>🏢 Херо сграда (/hero.webp)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateGlobal('defaultOgImage', '/imotencenterlogo.png')}
                        className={`text-[11px] px-2.5 py-1 rounded-md border transition-all flex items-center gap-1 font-medium ${
                          content.seo?.defaultOgImage === '/imotencenterlogo.png'
                            ? 'bg-[#C5A46E]/20 border-[#C5A46E] text-[#0F1E32] font-bold'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span>👑 Лого на фирмата</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdateGlobal('defaultOgImage', '/imotencenterlogo-cropped.png')}
                        className={`text-[11px] px-2.5 py-1 rounded-md border transition-all flex items-center gap-1 font-medium ${
                          content.seo?.defaultOgImage === '/imotencenterlogo-cropped.png'
                            ? 'bg-[#C5A46E]/20 border-[#C5A46E] text-[#0F1E32] font-bold'
                            : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                      >
                        <span>🌟 Златен знак</span>
                      </button>
                    </div>

                    <div className="pt-2.5 border-t border-slate-150 flex flex-wrap items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => handleApplyOgToAllPages(content.seo?.defaultOgImage || '/hero.webp')}
                        className="text-xs font-bold text-[#0F1E32] hover:text-[#C5A46E] bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg border border-slate-200 transition flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Приложи това изображение за ВСИЧКИ страници</span>
                      </button>
                      <span className="text-[10px] text-slate-400">
                        Изчиства индивидуалните снимки на страниците, за да важи общата.
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1">
                  Основна ключова дума на агенцията
                </label>
                <input
                  type="text"
                  value={content.seo?.defaultFocusKeyword || ''}
                  onChange={(e) => handleUpdateGlobal('defaultFocusKeyword', e.target.value)}
                  placeholder="имоти Варна"
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                />
              </div>
            </div>

            {/* Structured Data (Schema.org JSON-LD) */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#C5A46E]" />
                    Автоматични Структурирани данни (Schema.org JSON-LD)
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Генерира `RealEstateAgent` и `WebPage` семантични схеми за показване на разширени резултати (Rich Snippets) в Google.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={content.seo?.structuredDataEnabled !== false}
                    onChange={(e) => handleUpdateGlobal('structuredDataEnabled', e.target.checked)}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <span className="text-xs font-bold text-slate-700">Включено</span>
                </label>
              </div>
            </div>

            {/* Sitemap.xml & Robots.txt Links */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 space-y-4">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Технически файлове за Googlebot (Robots.txt & Sitemap)
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-slate-800 block">Динамичен Sitemap XML</span>
                    <span className="text-[11px] font-mono text-slate-400">/sitemap.xml</span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href="/sitemap.xml"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Отвори
                    </a>
                    <button
                      onClick={() => copyToClipboard(`${window.location.origin}/sitemap.xml`, 'sitemap')}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      {copiedUrl === 'sitemap' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold text-slate-800 block">Robots.txt конфигурация</span>
                    <span className="text-[11px] font-mono text-slate-400">/robots.txt</span>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href="/robots.txt"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Отвори
                    </a>
                    <button
                      onClick={() => copyToClipboard(`${window.location.origin}/robots.txt`, 'robots')}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold flex items-center gap-1"
                    >
                      {copiedUrl === 'robots' ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Google & Meta Tracking Integrations (Consent Mode v2 Compliant) */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#C5A46E]" />
                    Интеграции & Анализи (Google Analytics, Ads, Meta Pixel, Search Console)
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Всички инструменти са напълно съвместими с европейския регламент (GDPR) и Google Consent Mode v2.
                  </p>
                </div>
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-md">
                  GDPR & Consent Mode v2
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Google Analytics 4 (GA4) ID
                  </label>
                  <input
                    type="text"
                    value={content.seo?.tracking?.googleAnalyticsId || ''}
                    onChange={(e) => handleUpdateTracking('googleAnalyticsId', e.target.value)}
                    placeholder="G-XXXXXXXXXX"
                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Пример: G-XXXXXXXXXX. Използва се само при дадено аналитично съгласие.
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Google Ads (Conversion ID / Tag)
                  </label>
                  <input
                    type="text"
                    value={content.seo?.tracking?.googleAdsId || ''}
                    onChange={(e) => handleUpdateTracking('googleAdsId', e.target.value)}
                    placeholder="AW-XXXXXXXXXX"
                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Пример: AW-XXXXXXXXXX. За измерване на реализации от Google Ads.
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Meta Pixel (Facebook / Instagram) ID
                  </label>
                  <input
                    type="text"
                    value={content.seo?.tracking?.metaPixelId || ''}
                    onChange={(e) => handleUpdateTracking('metaPixelId', e.target.value)}
                    placeholder="123456789012345"
                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Числов идентификатор на Facebook Pixel. Активира се само при маркетингово съгласие.
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">
                    Google Search Console (Код за потвърждение)
                  </label>
                  <input
                    type="text"
                    value={content.seo?.tracking?.googleSearchConsoleVerification || ''}
                    onChange={(e) => handleUpdateTracking('googleSearchConsoleVerification', e.target.value)}
                    placeholder="abc123XYZ... (от meta google-site-verification)"
                    className="w-full bg-white border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-1">
                    Въведете кода от Google Search Console HTML тага за потвърждение на собствеността.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ================= 5. MARKETING & META PIXEL DEDICATED CENTER ================= */}
        {activeSubTab === 'tracking' && (
          <div className="space-y-6 animate-fade-in">
            {/* Meta Pixel Featured Card */}
            <div className="bg-gradient-to-br from-[#0F1E32] to-[#1A3353] rounded-2xl p-6 text-white border border-slate-700/80 shadow-xl space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-700/60 pb-5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-[#1877F2] flex items-center justify-center text-white shadow-lg shrink-0">
                    <svg className="w-7 h-7 fill-current" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base sm:text-lg font-black uppercase tracking-wide text-white">
                        Meta Pixel (Facebook & Instagram Pixel)
                      </h3>
                      {content.seo?.tracking?.metaPixelId ? (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                          Активен
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-700 text-slate-300 border border-slate-600">
                          Не е въведен
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-300 font-light mt-0.5">
                      Следете посетителите, разгледаните оферти и запитванията за реклами във Facebook и Instagram.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleTestPixel}
                  className="px-4 py-2 bg-[#1877F2] hover:bg-[#166fe5] text-white text-xs font-bold rounded-xl transition flex items-center gap-2 shadow-md cursor-pointer"
                >
                  <Zap className="w-3.5 h-3.5 fill-current" />
                  <span>Тествай Pixel връзката</span>
                </button>
              </div>

              {/* Pixel ID Input Box */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                <div className="lg:col-span-2 space-y-3">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-200">
                    Вашият Meta Pixel ID (Идентификационен номер на пиксела)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={content.seo?.tracking?.metaPixelId || ''}
                      onChange={(e) => handleUpdateTracking('metaPixelId', e.target.value)}
                      placeholder="Въведете вашия Pixel ID (напр. 1275984633722001)"
                      className="w-full bg-[#0B1522] border-2 border-slate-600 hover:border-[#1877F2] focus:border-[#1877F2] rounded-xl py-3 px-4 text-sm font-mono text-white placeholder-slate-500 transition focus:outline-none"
                    />
                    {content.seo?.tracking?.metaPixelId && (
                      <span className="absolute right-3 top-3.5 text-emerald-400 flex items-center gap-1 text-xs font-bold">
                        <Check className="w-4 h-4 stroke-[3]" />
                      </span>
                    )}
                  </div>

                  {pixelTestResult && (
                    <div className={`p-3.5 rounded-xl border text-xs flex items-start gap-2.5 animate-in fade-in duration-200 ${
                      pixelTestResult.success 
                        ? 'bg-emerald-950/60 border-emerald-500/50 text-emerald-200' 
                        : 'bg-rose-950/60 border-rose-500/50 text-rose-200'
                    }`}>
                      {pixelTestResult.success ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                      )}
                      <div className="leading-relaxed">
                        {pixelTestResult.message}
                      </div>
                    </div>
                  )}

                  <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-700/60 text-xs text-slate-300 space-y-2">
                    <h5 className="font-bold text-white uppercase text-[11px] flex items-center gap-1.5 text-[#C5A46E]">
                      <Info className="w-3.5 h-3.5" />
                      Къде да намерите Вашия Pixel ID?
                    </h5>
                    <ol className="list-decimal pl-4 space-y-1 text-[11px] text-slate-300">
                      <li>Отворете <strong>Meta Events Manager</strong> (business.facebook.com).</li>
                      <li>Изберете Вашия акаунт и кликнете на <strong>Data Sources (Източници на данни)</strong>.</li>
                      <li>Копирайте <strong>ID номера</strong> на вашия Pixel (напр. <code>1275984633722001</code>) и го поставете тук.</li>
                      <li>Натиснете <strong>„Запази SEO Настройки“</strong> в горния десен ъгъл.</li>
                    </ol>
                  </div>
                </div>

                {/* Automatic Real Estate Tracking Events */}
                <div className="bg-[#122338] border border-slate-700/70 rounded-xl p-4 space-y-3">
                  <h5 className="font-bold text-white text-xs uppercase tracking-wider flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    Автоматични събития в сайта:
                  </h5>
                  <div className="space-y-2.5 text-[11px]">
                    <div className="p-2.5 bg-slate-900/60 rounded-lg border border-slate-800">
                      <span className="font-mono font-bold text-blue-400 block">fbq('track', 'PageView')</span>
                      <span className="text-slate-300 text-[10px] mt-0.5 block">Отчита всяко зареждане на страница.</span>
                    </div>
                    <div className="p-2.5 bg-slate-900/60 rounded-lg border border-slate-800">
                      <span className="font-mono font-bold text-amber-400 block">fbq('track', 'ViewContent')</span>
                      <span className="text-slate-300 text-[10px] mt-0.5 block">Отчита преглед на конкретен апартамент с цена и ID.</span>
                    </div>
                    <div className="p-2.5 bg-slate-900/60 rounded-lg border border-slate-800">
                      <span className="font-mono font-bold text-emerald-400 block">fbq('track', 'Lead')</span>
                      <span className="text-slate-300 text-[10px] mt-0.5 block">Отчита изпратена заявка за консултация или запитване за имот.</span>
                    </div>
                  </div>
                  <div className="text-[10px] text-emerald-300 pt-1 flex items-center gap-1 font-medium">
                    <span>✓ Съобразено с европейското GDPR съгласие за бисквитки.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Services & Favicon Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Google Analytics & Google Ads */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                    <Tag className="w-4 h-4 text-[#C5A46E]" />
                    Google Инструменти (Analytics & Ads)
                  </h4>
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    Consent Mode v2
                  </span>
                </div>

                <div className="space-y-4 text-xs">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Google Analytics 4 (GA4) ID
                    </label>
                    <input
                      type="text"
                      value={content.seo?.tracking?.googleAnalyticsId || ''}
                      onChange={(e) => handleUpdateTracking('googleAnalyticsId', e.target.value)}
                      placeholder="G-XXXXXXXXXX"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                    <span className="text-[10px] text-slate-400 block mt-1">
                      Пример: G-XXXXXXXXXX
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Google Ads (Conversion ID / Tag)
                    </label>
                    <input
                      type="text"
                      value={content.seo?.tracking?.googleAdsId || ''}
                      onChange={(e) => handleUpdateTracking('googleAdsId', e.target.value)}
                      placeholder="AW-XXXXXXXXXX"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                    <span className="text-[10px] text-slate-400 block mt-1">
                      Пример: AW-XXXXXXXXXX
                    </span>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Google Search Console (Код за потвърждение)
                    </label>
                    <input
                      type="text"
                      value={content.seo?.tracking?.googleSearchConsoleVerification || ''}
                      onChange={(e) => handleUpdateTracking('googleSearchConsoleVerification', e.target.value)}
                      placeholder="google-site-verification код..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 px-3 text-xs font-mono focus:ring-1 focus:ring-[#C5A46E] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Favicon Quick Management Card - 1-Click Visual Gallery Picker */}
              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <Globe className="w-4 h-4 text-[#C5A46E]" />
                      Икона на сайта (Favicon)
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Кликнете върху някоя снимка от галерията за моментален избор като икона в браузъра.
                    </p>
                  </div>
                  
                  {/* Browser Tab Simulation Preview */}
                  <div className="bg-[#202124] rounded-lg px-3 py-1.5 flex items-center gap-2 border border-slate-700 shadow-md">
                    <img 
                      src={content.branding?.faviconUrl || '/imotencenterlogo-cropped.png'} 
                      alt="Favicon" 
                      className="w-4 h-4 object-contain rounded-sm shrink-0 bg-white/10 p-0.5"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/imotencenterlogo-cropped.png';
                      }}
                    />
                    <span className="text-[11px] font-medium text-slate-200 max-w-[130px] truncate">
                      {content.branding?.siteTitle || 'Варна Имотен Център'}
                    </span>
                    <span className="text-slate-500 text-[10px] leading-none shrink-0">✕</span>
                  </div>
                </div>

                {/* 1-Click Visual Thumbnail Gallery */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <span>🖼️</span> Изберете от галерията с 1 клик:
                    </span>
                    <span className="text-[11px] text-slate-500 font-medium">
                      Текуща икона: <code className="text-[#C5A46E] bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">{content.branding?.faviconUrl || '/imotencenterlogo-cropped.png'}</code>
                    </span>
                  </div>

                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-200 max-h-56 overflow-y-auto shadow-inner">
                    {galleryImages.map((img, idx) => {
                      const isSelected = (content.branding?.faviconUrl || '/imotencenterlogo-cropped.png') === img;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => updateSection('branding', { faviconUrl: img })}
                          title={img}
                          className={`group relative aspect-square rounded-xl overflow-hidden border-2 transition-all p-1 flex items-center justify-center cursor-pointer ${
                            isSelected
                              ? 'border-[#C5A46E] ring-2 ring-[#C5A46E]/50 bg-amber-50/50 shadow-md scale-105 z-10'
                              : 'border-slate-200 bg-white hover:border-[#C5A46E]/60 hover:bg-slate-100'
                          }`}
                        >
                          <img 
                            src={img} 
                            alt={`Gallery ${idx}`}
                            className="w-full h-full object-contain rounded-lg"
                            onError={(e) => {
                              (e.target as HTMLElement).style.display = 'none';
                            }}
                          />
                          {isSelected && (
                            <div className="absolute top-1 right-1 bg-emerald-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-black shadow">
                              ✓
                            </div>
                          )}
                          <div className="absolute inset-0 bg-[#0F1E32]/0 group-hover:bg-[#0F1E32]/20 transition-colors pointer-events-none rounded-lg" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  <label className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-[#0F1E32] hover:bg-[#1B324E] text-white text-xs font-bold rounded-lg cursor-pointer transition shadow-sm">
                    <Upload className="w-3.5 h-3.5 text-[#C5A46E]" />
                    <span>Качи файл от компютър</span>
                    <input
                      type="file"
                      accept="image/*,.ico,.svg,.png,.webp"
                      className="hidden"
                      onChange={handleFaviconUpload}
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => setMediaPicker({
                      isOpen: true,
                      allowedType: 'image',
                      title: 'Изберете Favicon от медийните файлове в public/',
                      onSelect: (url) => updateSection('branding', { faviconUrl: url })
                    })}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold rounded-lg cursor-pointer transition shadow-sm"
                  >
                    <HardDrive className="w-3.5 h-3.5 text-[#C5A46E]" />
                    <span>📁 Отвори цялата медийна библиотека</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => updateSection('branding', { faviconUrl: '/imotencenterlogo-cropped.png' })}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-900 border border-amber-300 text-xs font-bold rounded-lg cursor-pointer transition"
                  >
                    <span>⭐ Официално лого на агенцията</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Footer Info Bar */}
      <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-2">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-[#C5A46E]" />
          <span>Всички промени се отразяват в реално време в HTML таговете на сайта.</span>
        </div>
        {lastSavedAt && (
          <span className="font-mono text-[11px] text-slate-400">
            Последно запазване: {lastSavedAt}
          </span>
        )}
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={mediaPicker.isOpen}
        onClose={() => setMediaPicker(prev => ({ ...prev, isOpen: false }))}
        onSelect={mediaPicker.onSelect}
        allowedType="image"
        title={mediaPicker.title}
      />
    </div>
  );
};

export default SEOManager;
